import { CreateReportSchedule, EmailExportedReport } from '@futrli-sdk/report_scheduling';

import {
  createScheduleReport,
  updateScheduleReport,
  deleteScheduledReport,
  approveDispatchedReport,
  denyDispatchedReport,
  deleteDispatchedReport
} from '../../predict/src/core/api/reporting/report-scheduling';
import { deleteExportedReport, emailExportedReport } from '../../predict/src/core/api/reporting/report-document-export';

import { createMutation } from '../utils/mutations';
import {
  reportSchedulesQueryKey,
  awaitingReviewReportsQueryKey,
  dispatchedReportsQueryKey,
  exportedReportsQueryKey
} from '../queries/reportSchedules';
import { queryClient } from '../core/queryClient';

const invalidateExportedPdfHistory = (organisationId: string, workspaceId: string, isConsolidation: boolean) => {
  queryClient.invalidateQueries({
    queryKey: dispatchedReportsQueryKey(organisationId, workspaceId, isConsolidation)
  });
  queryClient.invalidateQueries({
    queryKey: exportedReportsQueryKey(organisationId, workspaceId, isConsolidation)
  });
};

// ── Create Schedule ──

export interface CreateScheduleInput {
  data: Partial<CreateReportSchedule>;
}

type CreateScheduleResult = Awaited<ReturnType<typeof createScheduleReport>>;

export const createScheduleReportMutation = (
  workspaceId: string,
  organisationId: string,
  isConsolidation: boolean
) =>
  createMutation<CreateScheduleInput, CreateScheduleResult>({
    mutationFn: async (input: CreateScheduleInput) =>
      createScheduleReport(workspaceId, organisationId, input.data, isConsolidation),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reportSchedulesQueryKey(organisationId, workspaceId, isConsolidation)
      });
      queryClient.invalidateQueries({
        queryKey: awaitingReviewReportsQueryKey(organisationId, workspaceId, isConsolidation)
      });
    }
  });

// ── Update Schedule ──

export interface UpdateScheduleInput {
  scheduleReportId: string;
  data: Partial<CreateReportSchedule>;
}

type UpdateScheduleResult = Awaited<ReturnType<typeof updateScheduleReport>>;

export const updateScheduleReportMutation = (
  workspaceId: string,
  organisationId: string,
  isConsolidation: boolean
) =>
  createMutation<UpdateScheduleInput, UpdateScheduleResult>({
    mutationFn: async (input: UpdateScheduleInput) =>
      updateScheduleReport(workspaceId, organisationId, input.scheduleReportId, input.data, isConsolidation),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reportSchedulesQueryKey(organisationId, workspaceId, isConsolidation)
      });
    }
  });

// ── Delete Schedule ──

export interface DeleteScheduleInput {
  scheduleReportId: string;
}

export const deleteScheduledReportMutation = (
  workspaceId: string,
  organisationId: string,
  isConsolidation: boolean
) =>
  createMutation<DeleteScheduleInput, Awaited<ReturnType<typeof deleteScheduledReport>>>({
    mutationFn: async (input: DeleteScheduleInput) =>
      deleteScheduledReport(workspaceId, organisationId, input.scheduleReportId, isConsolidation),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reportSchedulesQueryKey(organisationId, workspaceId, isConsolidation)
      });
    }
  });

// ── Approve Dispatched Report ──

export interface ApproveDispatchedInput {
  dispatchedReportId: string;
}

export const approveDispatchedReportMutation = (
  workspaceId: string,
  organisationId: string,
  isConsolidation: boolean
) =>
  createMutation<ApproveDispatchedInput, void>({
    mutationFn: async (input: ApproveDispatchedInput) =>
      approveDispatchedReport(workspaceId, organisationId, input.dispatchedReportId, isConsolidation),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: awaitingReviewReportsQueryKey(organisationId, workspaceId, isConsolidation)
      });
      queryClient.invalidateQueries({
        queryKey: exportedReportsQueryKey(organisationId, workspaceId, isConsolidation)
      });
    }
  });

// ── Deny Dispatched Report ──

export interface DenyDispatchedInput {
  dispatchedReportId: string;
}

export const denyDispatchedReportMutation = (
  workspaceId: string,
  organisationId: string,
  isConsolidation: boolean
) =>
  createMutation<DenyDispatchedInput, void>({
    mutationFn: async (input: DenyDispatchedInput) =>
      denyDispatchedReport(workspaceId, organisationId, input.dispatchedReportId, isConsolidation),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: awaitingReviewReportsQueryKey(organisationId, workspaceId, isConsolidation)
      });
    }
  });

// ── Delete Dispatched Report ──

export interface DeleteDispatchedInput {
  dispatchedReportId: string;
}

export const deleteDispatchedReportMutation = (
  workspaceId: string,
  organisationId: string,
  isConsolidation: boolean
) =>
  createMutation<DeleteDispatchedInput, void>({
    mutationFn: async (input: DeleteDispatchedInput) =>
      deleteDispatchedReport(workspaceId, organisationId, input.dispatchedReportId, isConsolidation),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: awaitingReviewReportsQueryKey(organisationId, workspaceId, isConsolidation)
      });
    }
  });

// ── Delete Exported Report ──

export interface DeleteExportedInput {
  exportedReportId: string;
}

export const deleteExportedReportMutation = (
  workspaceId: string,
  organisationId: string,
  isConsolidation: boolean
) =>
  createMutation<DeleteExportedInput, void>({
    mutationFn: async (input: DeleteExportedInput) =>
      deleteExportedReport(workspaceId, organisationId, input.exportedReportId, isConsolidation),
    onSuccess: () => {
      invalidateExportedPdfHistory(organisationId, workspaceId, isConsolidation);
    }
  });

// ── Email Exported Report ──

export interface EmailExportedReportInput {
  exportedReportId: string;
  data: EmailExportedReport;
}

export const emailExportedReportMutation = (
  workspaceId: string,
  organisationId: string,
  isConsolidation: boolean
) =>
  createMutation<EmailExportedReportInput, void>({
    mutationFn: async (input: EmailExportedReportInput) =>
      emailExportedReport(workspaceId, organisationId, input.exportedReportId, input.data, isConsolidation),
    onSuccess: () => {
      invalidateExportedPdfHistory(organisationId, workspaceId, isConsolidation);
    }
  });

// ── Send Report PDF (one-time schedule) ──

export interface SendReportPdfInput {
  data: Partial<CreateReportSchedule>;
}

export const sendReportPdfMutation = (
  workspaceId: string,
  organisationId: string,
  isConsolidation: boolean
) =>
  createMutation<SendReportPdfInput, Awaited<ReturnType<typeof createScheduleReport>>>({
    mutationFn: async (input: SendReportPdfInput) =>
      createScheduleReport(workspaceId, organisationId, input.data, isConsolidation),
    onSuccess: () => {
      invalidateExportedPdfHistory(organisationId, workspaceId, isConsolidation);
    }
  });
